import { ShieldCheck } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiErrorMessage, setAdminRole, setAdminToken } from '../../api/client'
import { adminApi } from '../../api/endpoints'
import { AuthLayout } from '../../components/AuthLayout'
import { JoinCodeStep } from '../../components/auth/JoinCodeStep'
import { ErrorMessage } from '../../components/ErrorMessage'
import { homeFor } from './adminAccess'

/**
 * The ops console's sign-in: the member sign-in's layout and picture, told from the other side - the
 * console at the centre and the work it does around it. Two quiet signals keep the two apart: the
 * green "Ops console" badge in the header, and the dark button instead of the members' yellow.
 */
export function AdminLoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  // Set once a code has been sent: the form swaps to the code step for this address.
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const address = email.trim()
      await adminApi.requestLoginCode(address)
      setSentTo(address)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send a join code'))
    } finally {
      setSubmitting(false)
    }
  }

  async function onVerify(code: string) {
    if (!sentTo) return
    const res = await adminApi.login({ email: sentTo, code })
    setAdminToken(res.token)
    setAdminRole(res.role)
    // Straight to the first screen the role may open - a reviewer to the queue, a creator to uploads.
    navigate(homeFor(res.role), { replace: true })
  }

  return (
    <AuthLayout
      headerEnd={
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
          <ShieldCheck size={13} /> Ops console
        </span>
      }
      showcase={{
        labels: ['Dashboard', 'Provider report', 'Verifications', 'Bulk upload', 'Reports', 'Team'],
        centre: 'Ops',
        title: ['Behind every dot,', 'a team that checks.'],
        subtitle:
          'Verify providers, bring seekers and tutors on board, review reports and keep the YellowDots network trusted.',
      }}
      blurb={
        <>
          <span className="font-semibold text-ink-900">Behind every dot, a team that checks.</span> Verify
          providers, manage uploads and keep the network trusted.
        </>
      }
    >
      <h1 className="flex items-center gap-2 text-3xl font-extrabold tracking-tight text-ink-900">
        <ShieldCheck size={26} className="shrink-0 text-emerald-600" />
        Ops sign in
      </h1>
      <p className="mt-1 text-ink-500">
        For the YellowDots team. We will email a join code to your ops address.
      </p>

      <div className="mt-6">
        {sentTo ? (
          <JoinCodeStep
            sentTo={sentTo}
            onVerify={onVerify}
            onResend={async () => {
              await adminApi.requestLoginCode(sentTo)
            }}
            onBack={() => setSentTo(null)}
            submitLabel="Sign in"
            buttonClassName="btn-ink w-full"
          />
        ) : (
          <form onSubmit={onSubmit} className="space-y-3.5">
            <label className="block">
              <span className="label label-required">Work email</span>
              <input
                required
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@careerlauncher.com"
              />
            </label>

            <ErrorMessage message={error} />

            <button type="submit" disabled={submitting} className="btn-ink w-full">
              {submitting ? 'Sending...' : 'Send join code'}
            </button>
          </form>
        )}
      </div>

      <div className="mt-6 border-t border-ink-200/70 pt-4 text-center text-sm text-ink-500">
        <p>
          Ops accounts are created by a super admin - there is no sign-up here.
        </p>
        <p className="mt-1 text-xs">
          Looking for the member app?{' '}
          <a href="/login" className="font-semibold text-ink-900 hover:underline">
            Sign in here
          </a>
        </p>
      </div>
    </AuthLayout>
  )
}
